// Engine Adapter — bridges engine API to IllumiTrain app interfaces
// Multi-cert: per-cert contexts replace global singletons
// Exposes window.AdaptiveEngine for assessment-autostudy.js guard code

import {
  initStoreOnce, initCertContext, isCertInitialized, getCertContext,
  startSession, getNextQuestion,
  recordAnswer, endSession, getLearnerProfile,
  loadTheta, getEngineInsight, getEngineState,
  getGraphStats, getConceptMastery, getWeakConcepts, computeCoverage, getCrossDomainLinks,
  flushAnalytics, getDistractorFrequency, getSessionAnalytics,
  forecastWeaknesses, getReinforcementTarget,
  detectMisconceptions, getMisconceptionSummary,
  getQuestionExplanation, getMisconceptionExplanation, getReadinessInsight
} from './index.js';
import { captureSessionSE, computeSEDelta } from './selector.js';
import { trackQuestionShown, trackSession } from './analytics.js';
import { read, write as fbWrite } from './firebase-store.js';
import { hasMinimumMetadata } from './constants.js';
import { setExplanationCert } from './explanations.js';

// ── Feature Flag ──
const USE_ENGINE = true;

// ── Per-cert contexts ──
// { [certId]: { questionBank, questionIndex, certSpec, initialized, activeSessionToken } }
const _certContexts = {};
let _activeCertId = null;
let _userId = null;
let _storeInitialized = false;

// ── Initialization ──

/**
 * Initialize the adaptive engine. Called once on app load.
 * @param {object} opts - { firebase, questions, userId, certSpec }
 */
function init(opts) {
  if (!USE_ENGINE) return;

  var firebase = opts.firebase;
  var questions = opts.questions;
  var userId = opts.userId;
  var certSpec = opts.certSpec || null;

  _userId = userId;

  // Initialize Firebase store once
  if (!_storeInitialized) {
    initStoreOnce(firebase);
    _storeInitialized = true;
  }

  // Detect active cert from app's CertContext
  var certId = _detectActiveCert();
  _activeCertId = certId;

  // Initialize this cert context
  _initCertContext(certId, questions, certSpec);

  // Load saved theta (ability estimates) from Firebase
  if (userId) {
    loadTheta(userId, certId).catch(function(e) {
      console.warn('[AdaptiveEngine] Failed to load theta:', e);
    });
  }

  console.log('[AdaptiveEngine] Initialized with ' + questions.length + ' questions for user ' + userId + ' (cert: ' + certId + ')');
}

/**
 * Initialize a cert context (internal).
 * @param {string} certId
 * @param {Array} questions
 * @param {object} certSpec - { domainWeights, displayName }
 */
function _initCertContext(certId, questions, certSpec) {
  if (_certContexts[certId] && _certContexts[certId].initialized) return;

  // Build lookup index
  var questionIndex = {};
  for (var i = 0; i < questions.length; i++) {
    questionIndex[questions[i].id] = questions[i];
  }

  var spec = certSpec || { domainWeights: null, displayName: certId };

  _certContexts[certId] = {
    questionBank: questions,
    questionIndex: questionIndex,
    certSpec: spec,
    initialized: true,
    activeSessionToken: null
  };

  // Initialize the core engine cert context
  initCertContext(certId, questions, spec);

  // Set explanation cert name
  setExplanationCert(certId);

  // Load enriched question metadata from Firebase and merge into question index
  _loadEnrichedMetadata(certId).catch(function(e) {
    console.warn('[AdaptiveEngine] Failed to load enriched metadata for ' + certId + ':', e);
  });
}

/**
 * Update user ID (e.g., after auth change).
 */
function setUser(userId) {
  _userId = userId;
  if (userId && _activeCertId) {
    loadTheta(userId, _activeCertId).catch(function() {});
  }
}

// ── Cert Detection ──

function _detectActiveCert() {
  try {
    if (typeof window !== 'undefined' && window.CertContext && window.CertContext.getCurrent) {
      var cert = window.CertContext.getCurrent();
      if (cert && cert.id) return cert.id;
    }
  } catch (e) { /* ignore */ }
  return 'md102';
}

/**
 * Check if the active cert's question bank has minimum metadata for the engine.
 * Replaces the old SUPPORTED_CERTS check with data-driven gating.
 * @param {string} certId
 * @returns {boolean}
 */
function _isCertSupported(certId) {
  var ctx = _certContexts[certId];
  if (!ctx || !ctx.questionBank) return false;
  return hasMinimumMetadata(ctx.questionBank);
}

/**
 * Called when user switches certs.
 * @param {string} certId
 * @param {Array} questions
 * @param {object} certSpec - { domainWeights, displayName }
 */
function onCertChange(certId, questions, certSpec) {
  // End any active session on the old cert
  if (_activeCertId && _certContexts[_activeCertId] && _certContexts[_activeCertId].activeSessionToken) {
    endCurrentSession().catch(function() {});
  }

  _activeCertId = certId;
  setExplanationCert(certId);

  if (questions && questions.length > 0) {
    _initCertContext(certId, questions, certSpec);

    // Load theta for new cert
    if (_userId) {
      loadTheta(_userId, certId).catch(function() {});
    }

    console.log('[AdaptiveEngine] Cert switched to "' + certId + '" — engine active with ' + questions.length + ' questions');
  } else {
    console.log('[AdaptiveEngine] Cert switched to "' + certId + '" — no questions provided');
  }
}

// ── Load Enriched Metadata from Firebase ──

async function _loadEnrichedMetadata(certId) {
  var ctx = _certContexts[certId];
  if (!ctx) return;

  try {
    var enriched = await read('/engine/questions');
    if (!enriched) {
      console.warn('[AdaptiveEngine] No enriched questions found in Firebase');
      return;
    }

    var merged = 0;
    for (var eId in enriched) {
      var eq = enriched[eId];
      if (!eq) continue;

      var target = ctx.questionIndex[eId];

      if (!target && eq.id && ctx.questionIndex[eq.id]) {
        target = ctx.questionIndex[eq.id];
      }

      if (!target && eq.originalId && ctx.questionIndex[eq.originalId]) {
        target = ctx.questionIndex[eq.originalId];
      }

      if (target) {
        if (eq.questionType) target.questionType = eq.questionType;
        if (eq.complexity) target.complexity = eq.complexity;
        if (eq.expectedRtMs) target.expectedRtMs = eq.expectedRtMs;
        if (eq.irtDifficulty != null) target.irtDifficulty = eq.irtDifficulty;
        if (eq.irtDiscrimination != null) target.irtDiscrimination = eq.irtDiscrimination;
        if (eq.skills && eq.skills.length > 0) target.skills = eq.skills;
        if (eq.conceptTags && eq.conceptTags.length > 0) target.conceptTags = eq.conceptTags;
        if (eq.domain) target.domain = eq.domain;
        merged++;
      }
    }

    console.log('[AdaptiveEngine] Merged enrichment for ' + merged + '/' + Object.keys(enriched).length + ' questions (cert: ' + certId + ')');
  } catch (e) {
    console.warn('[AdaptiveEngine] Enrichment merge failed:', e);
  }
}

// ── Plan Session ──

function planSession() {
  if (!_activeCertId || !_certContexts[_activeCertId]) return null;
  if (!USE_ENGINE || !_isCertSupported(_activeCertId)) return null;

  var domainStats = window.Questions ? window.Questions.getDomainStats() : {};
  var sessions = (window.State && window.State.getPreference)
    ? (window.State.getPreference('autoStudySessions') || [])
    : [];

  var domainKeys = Object.keys(window.Questions ? window.Questions.DOMAIN_NAMES : {});
  var domains = [];
  domainKeys.forEach(function(d) {
    var stats = domainStats[d] || { total: 0, answered: 0, correct: 0 };
    var pct = stats.answered > 0 ? Math.round(stats.correct / stats.answered * 100) : 0;
    domains.push({
      domain: d,
      name: (window.Questions.DOMAIN_NAMES || {})[d] || 'Domain ' + d,
      answered: stats.answered,
      correct: stats.correct,
      total: stats.total,
      pct: pct
    });
  });

  var sorted = domains.slice().sort(function(a, b) { return a.pct - b.pct; });
  var nextDomain = sorted[0] || { domain: 1, name: 'Unknown', pct: 0 };

  var lastDomain = sessions.length > 0 ? sessions[sessions.length - 1].domain : null;
  if (lastDomain && nextDomain.domain == lastDomain && sorted.length > 1 && sorted[0].pct > 0) {
    if (sorted[1].pct - sorted[0].pct < 20) nextDomain = sorted[1];
  }

  var mode = 'mixed';
  var allMastered = domains.every(function(d) { return d.pct >= 90; });
  if (allMastered) mode = 'review';
  else if (sessions.length === 0) mode = 'learn';
  else if (nextDomain.pct < 40) mode = 'learn';

  var weight = '';
  if (window.Assessment && window.Assessment._DOMAIN_REPORTS && window.Assessment._DOMAIN_REPORTS[nextDomain.domain]) {
    weight = window.Assessment._DOMAIN_REPORTS[nextDomain.domain].weight || '';
  }
  var reason = '';
  if (nextDomain.answered === 0) reason = 'You haven\'t studied this domain yet. Let\'s get started.';
  else if (nextDomain.pct < 50) reason = 'Your weakest area at ' + nextDomain.pct + '%. Focused practice here will boost your score.';
  else if (nextDomain.pct < 70) reason = 'You\'re at ' + nextDomain.pct + '%. A few more sessions will push you toward proficiency.';
  else if (nextDomain.pct < 85) reason = 'Close to mastery at ' + nextDomain.pct + '%. Let\'s lock in the remaining concepts.';
  else reason = 'Strong at ' + nextDomain.pct + '%. Maintaining with a review session.';

  var totalAnswered = 0, totalCorrect = 0;
  domains.forEach(function(d) { totalAnswered += d.answered; totalCorrect += d.correct; });
  var overallPct = totalAnswered > 0 ? Math.round(totalCorrect / totalAnswered * 100) : 0;
  var overallColor = overallPct >= 75 ? 'var(--grn)' : overallPct >= 50 ? 'var(--amb)' : 'var(--red)';
  var overallLabel = overallPct >= 90 ? 'Exam Ready' : overallPct >= 75 ? 'Nearly Ready' : overallPct >= 50 ? 'Building Foundation' : 'Getting Started';

  return {
    domains: domains,
    sorted: sorted,
    nextDomain: {
      domain: nextDomain.domain,
      name: nextDomain.name,
      pct: nextDomain.pct,
      reason: reason
    },
    sessions: sessions,
    sessionCount: sessions.length,
    overallPct: overallPct,
    overallColor: overallColor,
    overallLabel: overallLabel,
    whatNext: 'The adaptive engine will select questions based on your performance.',
    allMastered: allMastered,
    allSessionsComplete: false,
    useEngine: true,
    engineStats: { completed: sessions.length, total: 30, pct: Math.round(sessions.length / 30 * 100) },
    nextSession: null,
    nextSessionId: null,
    completedIds: [],
    _engineMode: mode,
    _engineDomainFocus: parseInt(nextDomain.domain) || null
  };
}

// ── Mode Labels ──

function getModeLabel(mode) {
  var labels = {
    'learn': 'Learning New Material',
    'review': 'Review Session',
    'mixed': 'Adaptive Study',
    'maintenance': 'Maintenance Review',
    'recalibration': 'Recalibration'
  };
  return labels[mode] || mode || 'Auto-Study';
}

// ── Session Management ──

async function startEngineSession(plan) {
  var certId = _activeCertId;
  if (!certId || !_certContexts[certId] || !_userId) return null;

  var domainFocus = plan?._engineDomainFocus || null;
  var mode = plan?._engineMode || 'mixed';

  var session = await startSession(_userId, certId, {
    mode: mode,
    maxDuration: 20,
    domainFocus: domainFocus,
    newVsReviewRatio: mode === 'review' ? 0.1 : 0.3
  });

  _certContexts[certId].activeSessionToken = session.sessionToken;

  // Capture SE snapshot at session start
  captureSessionSE(certId, 'start');

  // Analytics: session start
  trackSession(_userId, certId, 'start', {
    sessionId: session.sessionToken,
    mode: mode,
    domainFocus: domainFocus
  });

  return {
    sessionToken: session.sessionToken,
    mode: mode,
    domainFocus: domainFocus,
    shape: session.shape
  };
}

async function getNext() {
  var certId = _activeCertId;
  if (!certId || !_certContexts[certId]) return null;
  var ctx = _certContexts[certId];
  var token = ctx.activeSessionToken;
  if (!token || !_userId || !ctx.questionBank) return null;

  var result = await getNextQuestion(_userId, certId, token, ctx.questionBank);

  if (!result || !result.questionId || result.stopSignal) {
    return null;
  }

  var question = ctx.questionIndex[result.questionId];
  if (!question && window.Questions) {
    question = window.Questions.getById(result.questionId);
  }

  if (!question) {
    console.warn('[AdaptiveEngine] Question not found:', result.questionId);
    return null;
  }

  question._engineMeta = {
    reasonCode: result.reasonCode,
    source: result.source,
    phase: result.phase
  };

  trackQuestionShown(_userId, certId, {
    questionId: result.questionId,
    sessionId: token,
    phase: result.phase,
    reasonCode: result.reasonCode
  });

  return question;
}

async function onAnswer(questionId, correct, responseMs, confidence) {
  var certId = _activeCertId;
  if (!_userId || !USE_ENGINE || !certId || !_certContexts[certId]) return null;
  if (!_isCertSupported(certId)) return null;

  var ctx = _certContexts[certId];

  // If no active auto-study session, create a passive one
  var token = ctx.activeSessionToken;
  if (!token) {
    try {
      var session = await startSession(_userId, certId, {
        mode: 'mixed',
        maxDuration: 20,
        domainFocus: null,
        newVsReviewRatio: 0.3
      });
      token = session.sessionToken;
      ctx.activeSessionToken = token;
    } catch (e) {
      console.warn('[AdaptiveEngine] Failed to create passive session:', e);
      return null;
    }
  }

  try {
    var result = await recordAnswer(_userId, certId, token, {
      questionId: questionId,
      correct: correct,
      responseMs: responseMs || 20000,
      confidence: confidence || 'unsure',
      explanationMs: 0
    });
    return result;
  } catch (e) {
    console.warn('[AdaptiveEngine] recordAnswer failed:', e);
    return null;
  }
}

async function endCurrentSession() {
  var certId = _activeCertId;
  if (!certId || !_certContexts[certId]) return null;
  var ctx = _certContexts[certId];
  var token = ctx.activeSessionToken;
  if (!token || !_userId) return null;

  // Capture SE snapshot at session end and compute delta
  captureSessionSE(certId, 'end');
  var seDelta = computeSEDelta(certId);

  var summary = await endSession(_userId, certId, token);

  // Persist SE-delta metrics for IRT validation
  if (summary && seDelta) {
    try {
      var metricsPath = 'engine/users/' + _userId + '/certs/' + certId + '/profile/metrics/sessions';
      var existing = await read(metricsPath) || [];
      existing.push({
        sessionId: token,
        deltaSE: seDelta,
        accuracy: summary.accuracy || 0,
        questionCount: summary.questionCount || 0,
        timestamp: Date.now()
      });
      if (existing.length > 200) existing.splice(0, existing.length - 200);
      await fbWrite(metricsPath, existing);
    } catch (e) {
      // Non-fatal
    }
  }

  // Analytics: session end + flush
  trackSession(_userId, certId, 'end', {
    sessionId: token,
    questionCount: summary ? summary.questionCount : 0,
    accuracy: summary ? summary.accuracy : 0,
    endReason: summary ? summary.endReason : 'unknown'
  });
  flushAnalytics().catch(function() {});

  ctx.activeSessionToken = null;
  return summary;
}

// ── Profile ──

async function getUserProfile() {
  var certId = _activeCertId;
  if (!certId || !_certContexts[certId] || !_userId) return null;
  var ctx = _certContexts[certId];

  try {
    var profile = await getLearnerProfile(_userId, certId, ctx.certSpec);
    return {
      domainMastery: profile.domainMastery,
      theta: profile.theta,
      predictedPassRate: profile.predictedPassRate,
      cardsSeen: profile.cardsSeen,
      cardsMastered: profile.cardsMastered,
      cardsDueToday: profile.cardsDueToday,
      cardsInLearning: profile.cardsInLearning,
      totalSessions: profile.totalSessions,
      lastSessionDate: profile.lastSessionDate,
      conceptMastery: profile.conceptMastery,
      skillCoverage: profile.skillCoverage,
      weakConcepts: profile.weakConcepts,
      readinessBreakdown: profile.readinessBreakdown
    };
  } catch (e) {
    console.warn('[AdaptiveEngine] getUserProfile failed:', e);
    return null;
  }
}

// ── Expose on window for app guard code ──

var AdaptiveEngine = {
  init: init,
  setUser: setUser,
  planSession: planSession,
  getModeLabel: getModeLabel,
  startEngineSession: startEngineSession,
  getNext: getNext,
  onAnswer: onAnswer,
  endCurrentSession: endCurrentSession,
  getUserProfile: getUserProfile,
  isActive: function() {
    return USE_ENGINE && _activeCertId && _certContexts[_activeCertId] && _isCertSupported(_activeCertId);
  },
  getActiveCert: function() { return _activeCertId; },
  isCertSupported: _isCertSupported,
  onCertChange: onCertChange,
  hasActiveSession: function() {
    var ctx = _activeCertId && _certContexts[_activeCertId];
    return ctx ? !!ctx.activeSessionToken : false;
  },
  USE_ENGINE: USE_ENGINE,
  // Knowledge graph functions (cert-scoped)
  getGraphStats: function() { return _activeCertId ? getGraphStats(_activeCertId) : null; },
  getConceptMastery: function() {
    return (_userId && _activeCertId) ? getConceptMastery(_activeCertId, _userId) : Promise.resolve({});
  },
  getWeakConcepts: function(threshold) {
    return (_userId && _activeCertId) ? getWeakConcepts(_activeCertId, _userId, threshold) : Promise.resolve([]);
  },
  getCrossDomainLinks: function() { return _activeCertId ? getCrossDomainLinks(_activeCertId) : []; },
  // Analytics functions (cert-scoped)
  getDistractorFrequency: function() {
    return (_userId && _activeCertId) ? getDistractorFrequency(_userId, _activeCertId) : Promise.resolve({});
  },
  getSessionAnalytics: function(sid) {
    return (_userId && _activeCertId) ? getSessionAnalytics(_userId, _activeCertId, sid) : Promise.resolve(null);
  },
  flushAnalytics: function() { return flushAnalytics(); },
  // Weakness forecasting (cert-scoped)
  forecastWeaknesses: function() {
    if (!_userId || !_activeCertId) return Promise.resolve({ domains: {}, weakestDomain: null, recommendations: [] });
    var ctx = _certContexts[_activeCertId];
    return forecastWeaknesses(_userId, _activeCertId, ctx ? ctx.certSpec : null);
  },
  getReinforcementTarget: function() {
    if (!_userId || !_activeCertId) return Promise.resolve(null);
    var ctx = _certContexts[_activeCertId];
    return getReinforcementTarget(_userId, _activeCertId, ctx ? ctx.certSpec : null);
  },
  // Misconception detection (cert-scoped)
  getMisconceptions: function() {
    return (_userId && _activeCertId) ? detectMisconceptions(_userId, _activeCertId, null) : Promise.resolve([]);
  },
  getMisconceptionSummary: function() {
    return (_userId && _activeCertId) ? getMisconceptionSummary(_userId, _activeCertId, null) : Promise.resolve({ totalMisconceptions: 0, topMisconceptions: [], highSeverityCount: 0, errorClusters: {} });
  },
  // AI explanations (cert-scoped)
  getInsight: function(type, context) {
    return (_userId && _activeCertId) ? getEngineInsight(_userId, _activeCertId, type, context) : Promise.resolve({ type: type, explanation: null, cached: false });
  },
  getQuestionExplanation: function(questionId, context) {
    return (_userId && _activeCertId) ? getQuestionExplanation(_userId, _activeCertId, questionId, context || {}) : Promise.resolve({ explanation: null, cached: false, type: 'question' });
  },
  getMisconceptionExplanation: function(misconception) {
    return (_userId && _activeCertId) ? getMisconceptionExplanation(_userId, _activeCertId, misconception) : Promise.resolve({ explanation: null, cached: false, type: 'misconception' });
  },
  // Observability
  getEngineState: function() { return getEngineState(_activeCertId); },
  getReadinessInsight: function() {
    if (!_userId || !_activeCertId) return Promise.resolve({ explanation: null, cached: false, type: 'readiness' });
    return getUserProfile().then(function(profile) {
      return profile ? getReadinessInsight(_userId, _activeCertId, profile) : { explanation: null, cached: false, type: 'readiness' };
    });
  }
};

// Export for ESM bundle
export { AdaptiveEngine };

// Also attach to window for app's typeof checks
if (typeof window !== 'undefined') {
  window.AdaptiveEngine = AdaptiveEngine;
}
